import { useCallback, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLoginViewModel } from "../../viewmodel/useLoginViewModel";
import { useInlineLoading } from "@/shared/loading";
import { routes } from "@/app/navigation/routes";
import styles from "./LoginForm.module.css";

/**
 * LoginForm Component
 * Pure presentational component - all logic in ViewModel
 */
export const LoginForm = () => {
  const location = useLocation();
  const {
    credentials,
    isLoading,
    emailError,
    passwordError,
    updateEmail,
    updatePassword,
    handleSubmit,
    handleGoogleLogin,
    handleAppleLogin,
    handleForgotPassword,
  } = useLoginViewModel();

  const [showPassword, setShowPassword] = useState(false);
  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const BUTTON_ID = "login-button";
  const { isLoading: isInlineLoading } = useInlineLoading(BUTTON_ID);
  const buttonLoading = isLoading || isInlineLoading;

  return (
    <div className={styles.formContainer}>
      <h1 className={styles.title}>Welcome back</h1>
      <p className={styles.subtitle}>Enter your details to get sign in to your account.</p>

      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.inputGroup}>
          <label className={styles.label} htmlFor="email">
            Email
          </label>
          <input
            id="email"
            type="email"
            className={`${styles.input} ${emailError ? styles.inputError : ""}`}
            value={credentials.email}
            onChange={(e) => updateEmail(e.target.value)}
            placeholder="e.g. username@kinety.com"
          />
          {emailError && <div className={styles.errorMessage}>{emailError}</div>}
        </div>

        <div className={styles.inputGroup}>
          <label className={styles.label} htmlFor="password">
            Password
          </label>
          <div className={styles.passwordInputWrapper}>
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              className={`${styles.input} ${styles.passwordInput} ${passwordError ? styles.inputError : ""}`}
              value={credentials.password}
              onChange={(e) => updatePassword(e.target.value)}
              placeholder="enter your password"
            />
            <button
              type="button"
              className={styles.passwordToggle}
              onClick={togglePasswordVisibility}
              aria-label={showPassword ? "Hide password" : "Show password"}
              aria-pressed={showPassword}
            >
              {showPassword ? (
                <svg
                  width="17"
                  height="17"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                  <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg
                  width="17"
                  height="17"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>
          {passwordError && <div className={styles.errorMessage}>{passwordError}</div>}
          <label onClick={handleForgotPassword} className={styles.forgotPasswordLink}>
            Forgot Password?
          </label>
        </div>

        <button
          id={BUTTON_ID}
          type="submit"
          className={styles.continueButton}
          disabled={buttonLoading}
        >
          {buttonLoading ? "Loading..." : "Continue"}
        </button>
      </form>

      {/* Separator */}
      <div className={styles.separator}>
        <div className={styles.separatorLine}></div>
        <span className={styles.separatorText}>Or Sign in with</span>
        <div className={styles.separatorLine}></div>
      </div>

      {/* Social Login Buttons */}
      <div className={styles.socialButtons}>
        <button
          id="with-google-button"
          type="button"
          className={styles.socialButton}
          onClick={handleGoogleLogin}
        >
          <div className={styles.socialIcon}>
            <span className={styles.googleIcon}>G</span>
          </div>
          <span>Google</span>
        </button>

        <button
          id="with-apple-button"
          type="button"
          className={styles.socialButton}
          onClick={handleAppleLogin}
        >
          <div className={styles.socialIcon}>
            <svg
              className={styles.appleIcon}
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C1.79 15.25 4.96 6.39 9.38 6.39c1.78 0 3.05.86 4.6.86 1.52 0 2.5-.86 4.6-.86 4.1 0 7.2 8.46 2.47 13.43zm-2.1-15.4c.9-1.1 1.5-2.64 1.33-4.18-1.3.05-2.87.87-3.8 1.96-.82.95-1.54 2.47-1.35 3.93 1.4.11 2.85-.7 3.82-1.71z" />
            </svg>
          </div>
          <span>Apple</span>
        </button>
      </div>

      {/* Register Link */}
      <div className={styles.registerLink}>
        Don&apos;t have an account?
        <Link to={routes.signup} state={location.state} className={styles.registerLinkText}>
          Register Now
        </Link>
      </div>
    </div>
  );
};
